// Pure datasheet helpers shared by the composer, the preview and the index composer: the box
// model, zones, content bindings, layout math and the DS_Index family helpers.
import { parseDsIndex } from '../development/dsIndexName';
import { sheetLayout } from '../development/dsTemplateLayout';
import { SHARED_SHEET } from '../development/pdf/dsGroupBySheet';
import { isDsValues, isCalc } from '../development/dsCalc';

export const dsErrMsg = (e, fallback) =>
  e?.response?.status === 403 ? 'Access denied. You do not have permission to use the ISA Datasheet Composer.' : fallback;

export const FONT  = { fontFamily: 'Calibri, sans-serif', fontSize: '8pt' };

export const THIN  = '1px solid #aaa';

export const THICK = '2px solid #000';

export const ROW_H = 22;

export const base  = {
  ...FONT, padding: '0 3px',
  height: ROW_H, lineHeight: `${ROW_H}px`,
  verticalAlign: 'middle', overflow: 'hidden', whiteSpace: 'nowrap',
};

export const SECTION_TYPES = new Set(['blank','cal-group-header','cal-subheader','cal-data','comp-header','comp-subheader','comp-data']);

// The rows that open the Calibrations and the Component Identifications blocks.
export const SECTION_HEADS = new Set(['cal-group-header', 'comp-header']);

export const CAL_COLS  = [140, 150, 60, 60, 82, 75, 75]; // 642px total

export const CAL_HEADS = ['TAG NO/FUNCTIONAL IDENT', 'MEAS/SIGNAL/SCALE', 'LRV', 'URV', 'ACTION', 'LRV', 'URV'];

export const COMP_COLS  = [200, 200, 242];               // 642px total

export const COMP_HEADS = ['COMPONENT TYPE', 'MANUFACTURER', 'MODEL NUMBER'];

export const STATIC_SECTIONS = [
  { type: 'cal-group-header', lineNo: 110 },
  { type: 'cal-subheader',    lineNo: 111 },
  { type: 'cal-data',         lineNo: 112 },
  { type: 'cal-data',         lineNo: 113 },
  { type: 'cal-data',         lineNo: 114 },
  { type: 'cal-data',         lineNo: 115 },
  { type: 'cal-data',         lineNo: 116 },
  { type: 'cal-data',         lineNo: 117 },
  { type: 'comp-header',      lineNo: 118 },
  { type: 'comp-subheader',   lineNo: 119 },
  { type: 'comp-data',        lineNo: 120 },
  { type: 'comp-data',        lineNo: 121 },
  { type: 'comp-data',        lineNo: 122 },
  { type: 'comp-data',        lineNo: 123 },
  { type: 'comp-data',        lineNo: 124 },
];

// ── Serialization ─────────────────────────────────────────────────────────────
//
// zone = 'header'  → lineNo 1-10
// zone = 'center'  → single box in center panel (lineNo = 0)
// zone = 'body'    → lineNo 11-108 (left: 11-59, right: 60-108)

// A box that has never reached the DB still carries its synthesized `box-<ts>` id.
// Distinct from isUnsaved, which also covers edited-but-saved boxes for the highlight.
export function isTempId(id) {
  return typeof id === 'string' && id.startsWith('box-');
}

export function isUnsaved(box) {
  return (typeof box.id === 'string' && box.id.startsWith('box-')) || !!box._dirty;
}

// The composer canvas is pinned light in every theme, and its wrapper sets color:'#222'.
// An <input> does NOT inherit colour - it uses the browser's own field colour - so any input
// drawn on or over the canvas has to say so explicitly, or it renders light grey on white in
// the dark themes (Buckeye, Mariners) and cannot be read.
export const LIGHT_FIELD = { color: '#222', backgroundColor: '#fff' };

// A template is a plain master form or a per-Index-Sheet copy, told apart by its DS_Index
// (dsIndexName.js). A legacy {name}-{yyyyMMdd}{seq} key predates per-sheet copies, so it is plain.
export const isSheetCopy = d => parseDsIndex(d?.ds_Index)?.isPlain === false;

// Revisions of one datasheet share a family: form + Index Sheet code. A legacy key has no code,
// so its revisions group by DS_Name.
export const familyOf = d => parseDsIndex(d?.ds_Index)?.family ?? `${d?.ds_Name ?? ''}`;

export const revOf = d => parseDsIndex(d?.ds_Index)?.rev ?? (d?.revision || d?.ds_Index || '');

export const familyLabel = d => {
  const p = parseDsIndex(d?.ds_Index);
  return p && !p.isPlain ? `${p.form} · ${p.code}` : (d?.ds_Name ?? '');
};

// Plain ordinal order: revisions (00..99, A0..ZZ) sort correctly as strings, locale order does not.
export const byRevDesc = (a, b) => (revOf(a) < revOf(b) ? 1 : revOf(a) > revOf(b) ? -1 : 0);

export const LEGACY_KEY = /-\d{10}$/;

// The template types on offer come from the layout spec itself: every Template_Name except
// Application Specific (appended to every form) and the per-form overrides, named by form number.
export const templateTypesOf = specRows => [...new Set((specRows || []).map(r => r.templateName))]
  .filter(n => n && n !== 'Application Specific' && !/^\d/.test(n)).sort();

// FALLBACK ONLY. The zone is stored on the row now (DS_Zone) because the real ISA forms
// number continuously across sheets - 11-138, 211-280, 411-538 - and each sheet has its own
// geometry, so no band scheme describes them all. This remains for rows written before the
// column existed, whose numbers do follow the old bands.
// The Calibrations and Component Identifications grids are a fixed shell drawn by the
// renderer, not template data. They were drawn on EVERY sheet of EVERY template, always
// numbered 110-124 - which is why a valve showed them on all three sheets with sheet 1's
// numbers. Draw them only where the template actually has content for them.
export function hasStaticContent(boxes = []) {
  return boxes.some(b => b.zone === 'section-cell');
}

function zoneFromLine(lineNo) {
  if (lineNo === 0)                    return 'center';
  if (lineNo >= 1  && lineNo <= 10)   return 'header';
  if (lineNo >= 11 && lineNo <= 108)  return 'body';
  return 'section-cell';
}

// Stored zone -> the canvas's zone vocabulary. The canvas splits the body into two columns by
// zone key, so bodyL/bodyR both map to 'body' and the column is carried separately.
function zoneFromStored(dsZone, lineNo) {
  switch (dsZone) {
    case 'center': return 'center';
    case 'header': return 'header';
    case 'bodyL':
    case 'bodyR':  return 'body';
    case 'cal':
    case 'comp':   return 'section-cell';
    default:       return zoneFromLine(lineNo);
  }
}

// Splits a stored field binding ("admin:Table.Field" / "Table.Field" / "dsvalues")
// back into its parts for the field picker. A dsvalues or calc: binding has no table or field,
// even though its text contains a dot.
export function splitFieldSource(fs) {
  if (isDsValues(fs) || isCalc(fs)) return { source: fs, table: '', name: '' };
  const clean  = fs.startsWith('admin:') ? fs.slice(6) : fs;
  const dotIdx = clean.indexOf('.');
  return {
    source: fs,
    table:  dotIdx >= 0 ? clean.slice(0, dotIdx) : '',
    name:   dotIdx >= 0 ? clean.slice(dotIdx + 1) : '',
  };
}

// A box carries two content values, tag-based and index-based. DS_BoxType decides
// which field holds them: a value box binds a field, anything else holds literal
// text. The index side falls back to the tag side, and that equality IS the
// "same for Tag Based and Index Based" state - there is no flag column.
export function contentFor(box, side) {
  const isValue = box.type === 'value';
  if (side === 'idx') {
    return isValue
      ? (box.fieldSourceIdx ?? box.fieldSource ?? '')
      : (box.labelIdx ?? box.label ?? '');
  }
  return isValue ? (box.fieldSource ?? '') : (box.label ?? '');
}

export function contentLinked(box) {
  return contentFor(box, 'tag') === contentFor(box, 'idx');
}

// Collapses a box onto its index-based content, for surfaces that render per
// Index_Sheet instrument. Tag-based output (the composer canvas, the PDF
// exporter) keeps reading label/fieldSource, which hold the tag side.
export function toIndexSide(box) {
  const isValue = box.type === 'value';
  return isValue
    ? { ...box, fieldSource: contentFor(box, 'idx') }
    : { ...box, label: contentFor(box, 'idx') };
}

// Header and center are one copy per template, shown on every sheet, so they live on
// SHARED_SHEET. Everything else belongs to the page it sits on.
export const isSharedZone = zone => zone === 'header' || zone === 'center';

// The DS_Zone a box should be saved with. The canvas only knows 'body' and 'section-cell', so
// without this every box it created was stored with no zone and read back through the classic
// 11-59 / 60-108 bands - which puts a valve's left line 70 in the right column. The sheet's spec
// says which column or block a line belongs to. A line outside every range keeps what it had.
// Short column label for the grid view, from the same zone the box will be saved with. Falls back
// to the classic 59/60 split only for a body box with no spec and no stored zone.
const ZONE_LABELS = { header: 'header', center: 'center', bodyL: 'body L', bodyR: 'body R', cal: 'cal', comp: 'comp' };

export function zoneLabelFor(box, layout) {
  const z = storedZoneFor(box, layout);
  if (z) return ZONE_LABELS[z] ?? z;
  return box.zone === 'body' ? (box.lineNo <= 59 ? 'body L' : 'body R') : box.zone;
}

const inRange = (spec, n) => spec?.lineFrom != null && n >= spec.lineFrom && n <= spec.lineTo;

export function storedZoneFor(box, layout) {
  if (box.zone === 'header' || box.zone === 'center') return box.zone;
  if (box.zone === 'body') {
    if (inRange(layout?.bodyL, box.lineNo)) return 'bodyL';
    if (inRange(layout?.bodyR, box.lineNo)) return 'bodyR';
  }
  if (box.zone === 'section-cell') {
    if (inRange(layout?.cal,  box.lineNo)) return 'cal';
    if (inRange(layout?.comp, box.lineNo)) return 'comp';
  }
  return box.dsZone ?? null;
}

export const onSheet = (box, sheet) => {
  const s = box.sheet ?? 1;
  return s === sheet || s === SHARED_SHEET;
};

export function linesToBoxes(lines) {
  return lines.map(line => {
    const lineNo  = line.ds_Line ?? 1;
    const isValue = line.ds_BoxType === 'value';
    // Tag Based content; Index Based falls back to it when the two are the same.
    const tagRaw  = line.ds_ContentTAG ?? '';
    const idxRaw  = line.ds_ContentIDX ?? tagRaw;
    const tag     = splitFieldSource(isValue ? tagRaw : '');
    const idx     = splitFieldSource(isValue ? idxRaw : '');
    return {
      id:          line.id,
      type:        line.ds_BoxType || 'label',
      label:       isValue ? '' : tagRaw,
      labelIdx:    isValue ? '' : idxRaw,
      fieldSource:    tag.source,
      fieldTable:     tag.table,
      fieldName:      tag.name,
      fieldSourceIdx: idx.source,
      fieldTableIdx:  idx.table,
      fieldNameIdx:   idx.name,
      zone:        zoneFromStored(line.ds_Zone, lineNo),
      // The stored zone, kept verbatim so a save round-trips it unchanged. `zone` above is the
      // canvas's coarser view of the same thing.
      dsZone:      line.ds_Zone ?? null,
      lineNo,
      // Which page of a multi-sheet template this box sits on. Every row written
      // before the column existed defaults to 1, which is correct: those templates
      // are single-sheet, and that sheet is sheet 1.
      sheet:       line.ds_Sheet ?? 1,
      order:       line.ds_RowOrder ?? 1,
      colSpan:     line.ds_ColSpan ?? 12,
    };
  });
}

export function boxToLine(box, dsIndexId) {
  const contentTag = contentFor(box, 'tag');
  const contentIdx = contentFor(box, 'idx');
  return {
    dsIndexId,
    dsLine:    box.lineNo  ?? 1,
    dsSheet:   box.sheet   ?? 1,
    dsZone:    box.dsZone  ?? null,
    dsBoxType: box.type    || 'label',
    dsColSpan: box.colSpan ?? 12,
    dsContentTag: contentTag,
    dsContentIdx: contentIdx,
    sortOrder: box.order   ?? 1,
  };
}

// Stable comparison key for the save diff — every field boxToLine persists,
// minus dsIndexId, which cannot vary within one template.
export function lineKey(payload) {
  return JSON.stringify([
    payload.dsLine, payload.dsSheet, payload.dsZone, payload.dsBoxType, payload.dsColSpan,
    // Both content columns, or decoupling the index side would not register as a
    // change and the save would skip the row.
    payload.dsContentTag, payload.dsContentIdx,
    payload.sortOrder,
  ]);
}

// ── Width model ───────────────────────────────────────────────────────────────
//
// DS_ColSpan is absolute twelfths, but CanvasBox renders it as a flex-grow
// ratio. The two models agree only while a row's spans sum to 12, so every
// mutation of a header or body row runs back through here. Remainder goes to
// the leftmost boxes: 3 boxes -> 4/4/4, 5 boxes -> 3/3/2/2/2.
//
// Center slots hold a single box and section cells use fixed pixel widths from
// CAL_COLS/COMP_COLS, so neither participates.
export const rowOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0);

function spread(total, boxes) {
  const base  = Math.floor(total / boxes.length);
  const extra = total - base * boxes.length;
  const spans = new Map();
  [...boxes].sort(rowOrder).forEach((b, i) => spans.set(b.id, Math.max(1, base + (i < extra ? 1 : 0))));
  return spans;
}

function applySpans(list, spans) {
  return list.map(b => {
    if (!spans.has(b.id)) return b;
    const colSpan = spans.get(b.id);
    return colSpan === b.colSpan ? b : { ...b, colSpan, _dirty: true };
  });
}

export function rebalanceRow(list, zone, lineNo) {
  if (zone !== 'header' && zone !== 'body') return list;
  const row = list.filter(b => b.zone === zone && b.lineNo === lineNo);
  if (row.length === 0) return list;
  return applySpans(list, spread(12, row));
}

// Width set by hand in the edit popup wins; the rest of the row splits what is
// left, with a floor of 1 each so nothing collapses to zero.
export function redistributeAround(list, edited) {
  if (edited.zone !== 'header' && edited.zone !== 'body') return list;
  const siblings = list.filter(b => b.zone === edited.zone && b.lineNo === edited.lineNo && b.id !== edited.id);
  if (siblings.length === 0) return list;
  return applySpans(list, spread(Math.max(siblings.length, 12 - (edited.colSpan ?? 12)), siblings));
}

// Renumbers one row 1..n with box `id` at position `index` among the row's OTHER boxes, so
// DS_RowOrder follows a drop between two boxes. Only boxes whose order changes are marked
// dirty, which keeps the save diff to the rows that actually moved.
function orderRow(list, zone, lineNo, id, index) {
  const moved = list.find(b => b.id === id);
  if (!moved) return list;
  const others = list.filter(b => b.zone === zone && b.lineNo === lineNo && b.id !== id).sort(rowOrder);
  const at = Math.max(0, Math.min(index, others.length));
  const orders = new Map([...others.slice(0, at), moved, ...others.slice(at)].map((b, i) => [b.id, i + 1]));
  return list.map(b => (orders.has(b.id) && orders.get(b.id) !== b.order
    ? { ...b, order: orders.get(b.id), _dirty: true } : b));
}

// Adds or moves a box, then rebalances the row it landed in and — when it moved
// between rows — the row it left, so no row is orphaned at the wrong total. `index` is the
// insert position among the target row's other boxes; null appends at the end.
export function placeBox(prev, draggedBox, newBox, index = null) {
  const added = draggedBox.isNew
    ? [...prev, newBox]
    : [...prev.filter(b => b.id !== draggedBox.id), { ...draggedBox, ...newBox }];
  const next = index == null ? added : orderRow(added, newBox.zone, newBox.lineNo, newBox.id, index);
  const moved = !draggedBox.isNew
    && (draggedBox.zone !== newBox.zone || draggedBox.lineNo !== newBox.lineNo);
  const vacated = moved ? rebalanceRow(next, draggedBox.zone, draggedBox.lineNo) : next;
  return rebalanceRow(vacated, newBox.zone, newBox.lineNo);
}

export function getHeaderRow(boxes, lineNo) {
  return boxes
    .filter(b => b.zone === 'header' && b.lineNo === lineNo)
    .sort((a, b) => (a.order || 1) - (b.order || 1));
}

export function getCenterBoxes(boxes) {
  return boxes
    .filter(b => b.zone === 'center')
    .sort((a, b) => (a.order || 1) - (b.order || 1));
}

export function getBodyLine(boxes, lineNo) {
  return boxes
    .filter(b => b.zone === 'body' && b.lineNo === lineNo)
    .sort((a, b) => (a.order || 1) - (b.order || 1));
}

export function getSectionCells(boxes, lineNo) {
  return boxes
    .filter(b => b.zone === 'section-cell' && b.lineNo === lineNo)
    .sort((a, b) => (a.order || 1) - (b.order || 1));
}

// ── Copyable zones ────────────────────────────────────────────────────────────
//
// The checkbox groups in the duplicate dialog with their classic line ranges, used when the
// source's type has no spec - copyZones() derives the ranges from UtlCfg_DSTemplate otherwise.
// The server takes explicit line numbers so this mapping is not duplicated in C#.
export const COPY_ZONES = [
  { key: 'center',   label: 'Center panel',        lines: [0] },
  { key: 'header',   label: 'Header block',        lines: range(1, 10) },
  { key: 'bodyL',    label: 'Left body',           lines: range(11, 59) },
  { key: 'bodyR',    label: 'Right body',          lines: range(60, 108) },
  { key: 'cal',      label: 'Calibration section', lines: range(110, 117) },
  { key: 'comp',     label: 'Component section',   lines: range(118, 124) },
];

// Copy groups for one source template, from its spec: each group is every line of that zone on
// every sheet, so "Left body" on a valve is 11-74, 211-245 and 411-474 rather than 11-59. The
// numbering is continuous across a template's sheets, so a line number alone still names one
// row. No spec keeps COPY_ZONES.
export function copyZones(specRows, tempType, sheetCount, formName = null) {
  const perSheet = Array.from({ length: Math.max(1, sheetCount) }, (_, i) =>
    sheetLayout(specRows, tempType, i + 1, sheetCount, formName));
  if (perSheet.some(l => !l)) return COPY_ZONES;
  return COPY_ZONES.map(z => {
    const lines = new Set();
    for (const l of perSheet) {
      const spec = l[z.key];
      if (spec?.lineFrom != null) range(spec.lineFrom, spec.lineTo).forEach(n => lines.add(n));
    }
    return { ...z, lines: [...lines].sort((a, b) => a - b) };
  });
}

function range(from, to) {
  return Array.from({ length: to - from + 1 }, (_, i) => from + i);
}

// ── Mass populate ─────────────────────────────────────────────────────────────
//
// Zones that accept generated rows. Center slots and section cells are fixed
// single-occupancy positions, so they are not fill targets.
const PLACE_ZONES = [
  { key: 'header', label: 'Header block', zone: 'header', lines: range(1, 10) },
  { key: 'bodyL',  label: 'Left body',    zone: 'body',   lines: range(11, 59) },
  { key: 'bodyR',  label: 'Right body',   zone: 'body',   lines: range(60, 108) },
];

// The fill targets for one sheet, from its UtlCfg_DSTemplate spec: the valve's left body is
// 11-74, not 11-59. No spec keeps the classic bands.
export function placeZones(layout) {
  if (!layout) return PLACE_ZONES;
  const zone = (key, label, canvasZone, spec) =>
    spec?.lineFrom != null ? { key, label, zone: canvasZone, lines: range(spec.lineFrom, spec.lineTo) } : null;
  return [
    zone('header', 'Header block', 'header', layout.header),
    zone('bodyL',  'Left body',    'body',   layout.bodyL),
    zone('bodyR',  'Right body',   'body',   layout.bodyR),
  ].filter(Boolean);
}

// `box-<ts>` alone collides when several boxes are created in the same
// millisecond, which stamping rows does every time - duplicate ids would collide
// as React keys and confuse the save diff. The prefix is what isTempId matches.
let boxSeq = 0;

export function newBoxId() {
  boxSeq += 1;
  return `box-${Date.now()}-${boxSeq}`;
}

function makeBox({ type, label = '', fieldSource = '', zone, lineNo, order, colSpan = 12 }) {
  const parsed = splitFieldSource(fieldSource || '');
  return {
    id: newBoxId(),
    type: type || 'label',
    label:    type === 'value' ? '' : label,
    labelIdx: type === 'value' ? '' : label,
    fieldSource,
    fieldTable: parsed.table,
    fieldName:  parsed.name,
    fieldSourceIdx: fieldSource,
    fieldTableIdx:  parsed.table,
    fieldNameIdx:   parsed.name,
    zone, lineNo, order, colSpan,
  };
}

function linesUsed(boxes, zone) {
  return new Set(boxes.filter(b => b.zone === zone).map(b => b.lineNo));
}

export function freeLines(boxes, zoneKey, zones = PLACE_ZONES) {
  const pz = zones.find(z => z.key === zoneKey);
  if (!pz) return [];
  const used = linesUsed(boxes, pz.zone);
  return pz.lines.filter(n => !used.has(n));
}

export function usedLines(boxes, zoneKey, zones = PLACE_ZONES) {
  const pz = zones.find(z => z.key === zoneKey);
  if (!pz) return [];
  const used = linesUsed(boxes, pz.zone);
  return pz.lines.filter(n => used.has(n));
}

// The single primitive all four populate methods feed into: a list of rows, each
// row a list of cells, appended to the next free lines of a zone and rebalanced
// to 12 as they land. Rows that do not fit are reported rather than dropped
// silently.
export function appendRows(boxes, zoneKey, rows, zones = PLACE_ZONES) {
  const pz = zones.find(z => z.key === zoneKey);
  if (!pz || rows.length === 0) return { boxes, placed: 0, skipped: rows.length };
  const target = freeLines(boxes, zoneKey, zones).slice(0, rows.length);
  let next = [...boxes];
  target.forEach((lineNo, i) => {
    rows[i].forEach((cell, ci) => {
      next.push(makeBox({ ...cell, zone: pz.zone, lineNo, order: ci + 1 }));
    });
    next = rebalanceRow(next, pz.zone, lineNo);
  });
  return { boxes: next, placed: target.length, skipped: rows.length - target.length };
}

/**
 * Sibling of appendRows for explicitly chosen target lines.
 *
 * appendRows takes the next N FREE lines, which is why "repeat" could only ever fill forwards
 * into empty space. stampRows writes into exactly the lines the user picked and replaces whatever
 * is on them — that is what "rows to be modified" means, as against "rows to be appended".
 *
 * rows[i] pairs with lineNos[i]; if fewer rows than lines are supplied the last row repeats, so
 * one pattern can be stamped across any selection.
 */
export function stampRows(boxes, zoneKey, lineNos, rows, zones = PLACE_ZONES) {
  const pz = zones.find(z => z.key === zoneKey);
  if (!pz || lineNos.length === 0 || rows.length === 0) {
    return { boxes, placed: 0, replaced: 0 };
  }
  let next = [...boxes];
  let replaced = 0;
  lineNos.forEach((lineNo, i) => {
    const onLine = next.filter(b => b.zone === pz.zone && b.lineNo === lineNo);
    if (onLine.length > 0) {
      replaced += 1;
      next = next.filter(b => !(b.zone === pz.zone && b.lineNo === lineNo));
    }
    const cells = rows[i] || rows[rows.length - 1];
    cells.forEach((cell, ci) => {
      next.push(makeBox({ ...cell, zone: pz.zone, lineNo, order: ci + 1 }));
    });
    next = rebalanceRow(next, pz.zone, lineNo);
  });
  return { boxes: next, placed: lineNos.length, replaced };
}

// Cells of one existing row, as a template for repeating it.
export function rowCells(boxes, zoneKey, lineNo, zones = PLACE_ZONES) {
  const pz = zones.find(z => z.key === zoneKey);
  if (!pz) return [];
  return boxes
    .filter(b => b.zone === pz.zone && b.lineNo === lineNo)
    .sort(rowOrder)
    .map(b => ({ type: b.type, label: contentFor(b, 'tag'), fieldSource: b.type === 'value' ? b.fieldSource : '' }));
}

// Appends " 2", " 3" ... to label text when repeating, so stamped rows are
// distinguishable instead of N identical copies.
export function suffixCells(cells, n) {
  return cells.map(c => c.type === 'value' ? c : { ...c, label: c.label ? `${c.label} ${n}` : c.label });
}

// Tab-delimited wins when any line has a tab, which is what Excel puts on the
// clipboard; comma is the fallback for hand-typed or CSV input.
export function parseGrid(text) {
  const lines = String(text).replace(/\r/g, '').split('\n').filter(l => l.trim() !== '');
  if (lines.length === 0) return [];
  const delim = lines.some(l => l.includes('\t')) ? '\t' : ',';
  return lines.map(l => l.split(delim).map(c => c.trim()));
}

export const TABLE_W = 684;

export const REV_COLS = [
  { label: 'Rev',                  width: 36  },
  { label: 'Date',                 width: 72  },
  { label: 'Revision Description', width: null },
  { label: ['Drafted', 'By'],       width: 52  },
  { label: ['Approved', 'By'],      width: 62  },
  { label: 'Remarks',               width: 180 },
];

export const REV_LAST = REV_COLS.length - 1;
